/**
 * LeadCommentThread — the full project-comments-suite experience, scoped to a lead.
 *
 * This is a ground-up rebuild of the lead "Team Notes" thread that reuses the exact
 * building blocks + styling of the PM TaskComments suite (TipTap rich editor with
 * @mentions, threaded replies, pinning, kind tags, emoji reactions, filters, file
 * attachments, drag-and-drop, draft autosave, and a fullscreen pop-out) while keeping
 * the canonical `leads/{leadId}/comments` Firestore store so existing notes are
 * preserved. PDF plan pins (document annotations) are merged into the feed.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import {
  leadCommentsCollectionRef,
  leadCommentDocRef,
  migrateOrphanLeadCommentsIfNeeded,
} from '../../utils/leadCommentsFirestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  Image as ImageIcon,
  FileText,
  BarChart3,
  Presentation,
  Paperclip,
  Trash2,
  Send,
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  Pin,
  PinOff,
  CornerDownRight,
  Reply as ReplyIcon,
  X,
  Check,
} from 'lucide-react';
import { auth, db, storage } from '../../utils/firebase';
import {
  CommentAttachment,
  CommentMention,
  CommentReactionsMap,
  PdfAnnotation,
  TaskComment,
  TaskCommentKind,
} from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { formatDistanceToNow, format } from 'date-fns';
import { normalizeCommentReactions, toggleCommentReaction } from '../../utils/commentReactions';
import {
  subscribeToAnnotationsForLead,
  togglePdfAnnotationReaction,
  pdfAnnotationHasUnreadForUser,
  markAnnotationAsRead,
} from '../../utils/pdfAnnotationService';
import { getLeadDocuments } from '../../utils/leadDocumentService';
import { isCsvType } from '../../utils/projectFileService';
import { viewerDocumentIdForAttachment } from '../../utils/firestoreDocId';
import { createMentionNotification } from '../../utils/mentionNotification';
import { isInternalCommentMentionUser } from '../../utils/mentionAffiliation';
import { canDeleteTaskComment } from '../../utils/commentPermissions';
import {
  buildTaskCommentsById,
  getTaskCommentThreadRootId,
  groupTaskCommentRepliesByThreadRoot,
} from '../../utils/taskCommentThread';
import { buildCommentAttachmentStorageFileName } from '../../utils/storageFileNames';
import { useCommentTyping } from '../../hooks/useCommentTyping';
import { commentTypingThreadKey } from '../../utils/commentTypingService';
import CommentTypingIndicator from '../pm/CommentTypingIndicator';
import RichCommentEditor, { RichCommentEditorHandle, RichCommentEditorPayload } from '../pm/RichCommentEditor';
import RichCommentRenderer from '../pm/RichCommentRenderer';
import QuestionAnswerSlot from '../pm/QuestionAnswerSlot';
import UntaggedSendHint, { useUntaggedSendGate } from '../pm/UntaggedSendHint';
import { mentionsNotifySomeone } from '../../utils/untaggedCommentConfirm';
import CommentReactions from '../CommentReactions';
import UserHoverCard from '../ui/UserHoverCard';
import '../pm/TaskComments.css';
import './LeadCommentThread.css';

interface LeadCommentThreadProps {
  leadId: string;
  leadName?: string;
  compact?: boolean;
  defaultExpanded?: boolean;
  /** When this token changes (non-null), the thread auto-opens in the fullscreen pop-out. */
  popoutRequestToken?: number | string | null;
  onPopoutClose?: () => void;
  onOpenPdf?: (url: string, title: string, documentId: string, options?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  onOpenCsv?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  /** Opened from a mention notification. Scrolls to this note once the feed is loaded. */
  highlightCommentId?: string | null;
  onHighlightConsumed?: () => void;
}

/** Local feed shape — superset of the lead comment doc, compatible with the thread/permission helpers. */
interface LeadFeedComment {
  id?: string;
  authorId: string;
  authorName: string;
  authorInitials?: string;
  body: string;
  bodyHtml?: string;
  kind?: TaskCommentKind;
  parentId?: string | null;
  pinnedAt?: Date | null;
  pinnedById?: string | null;
  editedAt?: Date;
  reactions?: CommentReactionsMap;
  attachments?: CommentAttachment[];
  mentions?: CommentMention[];
  createdAt: Date;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

function firestoreValueToDate(value: unknown): Date {
  if (value == null) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const o = value as { seconds?: number; nanoseconds?: number };
  if (typeof o?.seconds === 'number') {
    return new Date(o.seconds * 1000 + Math.floor((o.nanoseconds || 0) / 1e6));
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
  }
  return new Date();
}

function firestoreValueToDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  return firestoreValueToDate(value);
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (type: string): React.ReactNode => {
  if (type.startsWith('image/')) return <ImageIcon size={14} />;
  if (type === 'application/pdf') return <FileText size={14} />;
  if (type.includes('word') || type.includes('document')) return <FileText size={14} />;
  if (type.includes('sheet') || type.includes('excel')) return <BarChart3 size={14} />;
  if (type.includes('presentation') || type.includes('powerpoint')) return <Presentation size={14} />;
  return <Paperclip size={14} />;
};

type FilterId = 'all' | 'mentions' | 'mine' | 'attachments';

const SCOPE_FILTER_CHIPS: { id: Exclude<FilterId, 'mine'>; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mentions', label: '@ Me' },
  { id: 'attachments', label: 'Has files' },
];

const KIND_OPTIONS: { id: TaskCommentKind; label: string; short: string }[] = [
  { id: 'note', label: 'Note', short: 'Note' },
  { id: 'decision', label: 'Decision', short: 'Decision' },
  { id: 'question', label: 'Question', short: 'Question' },
  { id: 'action', label: 'Action item', short: 'Action' },
];

const draftKey = (uid: string | undefined, leadId: string) => `leadCommentDraft:${uid || 'anon'}:${leadId}`;

function excerptOf(comment: LeadFeedComment, max = 80): string {
  const src = comment.body || '';
  const oneLine = src.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max)}…` : oneLine;
}

const LeadCommentThread: React.FC<LeadCommentThreadProps> = ({
  leadId,
  leadName,
  compact = false,
  defaultExpanded = false,
  popoutRequestToken = null,
  onPopoutClose,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  highlightCommentId = null,
  onHighlightConsumed,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();

  const [comments, setComments] = useState<LeadFeedComment[]>([]);
  const [commentsReady, setCommentsReady] = useState(false);
  const [pdfAnnotations, setPdfAnnotations] = useState<PdfAnnotation[]>([]);
  const [leadDocNames, setLeadDocNames] = useState<Map<string, string>>(new Map());
  const [leadDocIdToUrl, setLeadDocIdToUrl] = useState<Map<string, string>>(new Map());

  const [draftHtml, setDraftHtml] = useState('');
  const untaggedSend = useUntaggedSendGate(draftHtml);
  const [initialDraftHtml, setInitialDraftHtml] = useState('');
  const typingThreadKey = useMemo(() => commentTypingThreadKey('lead', leadId), [leadId]);
  const typingLabel = useCommentTyping(typingThreadKey, draftHtml);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(compact ? defaultExpanded : true);

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileDragOver, setFileDragOver] = useState(false);
  const fileDragDepthRef = useRef(0);

  const [replyTo, setReplyTo] = useState<LeadFeedComment | null>(null);
  const [composerKind, setComposerKind] = useState<TaskCommentKind | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingHtml, setEditingHtml] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [filter, setFilter] = useState<FilterId>('all');
  const [kindFilter, setKindFilter] = useState<TaskCommentKind | 'any'>('any');
  const [mineAuthorId, setMineAuthorId] = useState('');

  const [isPopout, setIsPopout] = useState(false);

  const editorRef = useRef<RichCommentEditorHandle>(null);
  const editEditorRef = useRef<RichCommentEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const editorScopeId = `lead-${leadId}`;

  const mentionableUsers = useMemo(
    () => crmUsers.filter(u => isInternalCommentMentionUser(u)),
    [crmUsers],
  );

  const photoForUid = useCallback((uid: string) => crmUsers.find(x => x.uid === uid)?.photoURL, [crmUsers]);
  const getUserDisplayName = useCallback(
    (id: string) => crmUsers.find(u => u.uid === id)?.displayName || id.slice(0, 8),
    [crmUsers],
  );

  /* ── Live lead comments subscription ──────────────────────────────────── */
  useEffect(() => {
    if (!leadId) return;
    setCommentsReady(false);
    void migrateOrphanLeadCommentsIfNeeded(leadId).catch(err => {
      console.warn('[LeadCommentThread] Orphan comment migration skipped:', err);
    });
    const q = query(leadCommentsCollectionRef(leadId), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      snap => {
        setComments(
          snap.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              authorId: String(data.userId ?? data.authorId ?? ''),
              authorName: data.userName || data.authorName || 'Someone',
              authorInitials: data.userInitials,
              body: data.content ?? data.body ?? '',
              bodyHtml: data.bodyHtml,
              kind: data.kind,
              parentId: data.parentId ?? null,
              pinnedAt: firestoreValueToDateOrNull(data.pinnedAt),
              pinnedById: data.pinnedById ?? null,
              editedAt: data.editedAt ? firestoreValueToDate(data.editedAt) : undefined,
              reactions: normalizeCommentReactions(data.reactions),
              attachments: data.attachments || undefined,
              mentions: data.mentions || undefined,
              createdAt: firestoreValueToDate(data.createdAt),
            } as LeadFeedComment;
          }),
        );
        setCommentsReady(true);
      },
      err => console.error('[LeadCommentThread] comments listener failed:', err),
    );
    return () => unsub();
  }, [leadId]);

  /* ── PDF plan pins for this lead ──────────────────────────────────────── */
  useEffect(() => {
    if (!leadId) return;
    return subscribeToAnnotationsForLead(leadId, setPdfAnnotations);
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getLeadDocuments(leadId)
      .then(docs => {
        if (cancelled) return;
        const nameMap = new Map<string, string>();
        const idMap = new Map<string, string>();
        docs.forEach(d => {
          nameMap.set(d.url, d.name);
          if (d.id) idMap.set(d.id, d.url);
        });
        setLeadDocNames(nameMap);
        setLeadDocIdToUrl(idMap);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  /* ── Auto-scroll feed to newest ───────────────────────────────────────── */
  const highlightCommentIdRef = useRef(highlightCommentId);
  highlightCommentIdRef.current = highlightCommentId;
  useEffect(() => {
    if (highlightCommentIdRef.current) return;
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [comments.length]);

  /* ── Mention deep link: scroll the named note into view ──────────────── */
  useEffect(() => {
    const id = highlightCommentId?.trim();
    if (!id || !commentsReady) return;
    if (filter !== 'all') {
      setFilter('all');
      return;
    }
    if (kindFilter !== 'any') {
      setKindFilter('any');
      return;
    }
    if (!comments.some(c => c.id === id)) {
      onHighlightConsumed?.();
      return;
    }

    let cancelled = false;
    let done = false;
    let giveUpId: number | undefined;

    const finish = () => {
      if (done || cancelled) return;
      done = true;
      onHighlightConsumed?.();
    };

    const tryScroll = (attempt: number) => {
      if (cancelled || done) return;
      const el = document.getElementById(`lead-team-comment-${id}`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.add('tc-item--flash');
        window.setTimeout(() => el.classList.remove('tc-item--flash'), 2000);
        if (giveUpId !== undefined) window.clearTimeout(giveUpId);
        finish();
        return;
      }
      if (attempt < 24) {
        window.setTimeout(() => tryScroll(attempt + 1), 80);
      }
    };

    giveUpId = window.setTimeout(finish, 8000);
    const start = window.setTimeout(() => tryScroll(0), 40);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
      if (giveUpId !== undefined) window.clearTimeout(giveUpId);
    };
  }, [highlightCommentId, comments, commentsReady, filter, kindFilter, onHighlightConsumed]);

  /* ── Per-lead draft autosave ──────────────────────────────────────────── */
  useEffect(() => {
    if (!leadId) return;
    try {
      const stored = window.localStorage.getItem(draftKey(user?.id, leadId));
      setInitialDraftHtml(stored || '');
      setDraftHtml(stored || '');
    } catch {
      setInitialDraftHtml('');
      setDraftHtml('');
    }
    setReplyTo(null);
    setComposerKind(null);
    setMineAuthorId('');
    setFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, user?.id]);

  useEffect(() => {
    if (!leadId) return;
    const handle = window.setTimeout(() => {
      try {
        const key = draftKey(user?.id, leadId);
        if (draftHtml && draftHtml !== '<p></p>') window.localStorage.setItem(key, draftHtml);
        else window.localStorage.removeItem(key);
      } catch {
        /* ignore quota / privacy-mode failures */
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [draftHtml, leadId, user?.id]);

  /* ── Pop-out: open on a fresh request token ───────────────────────────── */
  useEffect(() => {
    if (popoutRequestToken == null) return;
    setIsPopout(true);
  }, [popoutRequestToken]);

  const onPopoutCloseRef = useRef(onPopoutClose);
  onPopoutCloseRef.current = onPopoutClose;
  const wasPopoutRef = useRef(false);
  useEffect(() => {
    if (wasPopoutRef.current && !isPopout) onPopoutCloseRef.current?.();
    wasPopoutRef.current = isPopout;
  }, [isPopout]);

  useEffect(() => {
    if (!isPopout) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setIsPopout(false);
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tc-popout-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('tc-popout-open');
    };
  }, [isPopout]);

  /* ── Attachments: drag-and-drop + picker ──────────────────────────────── */
  const addFilesFromList = useCallback((fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter(f => {
      if (f.size > 100 * 1024 * 1024) {
        window.alert(`${f.name} exceeds the 100MB limit.`);
        return false;
      }
      return true;
    });
    if (files.length === 0) return;
    setStagedFiles(prev => [...prev, ...files]);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFilesFromList(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeStagedFile = (idx: number) => setStagedFiles(prev => prev.filter((_, i) => i !== idx));

  const hasFilesInTransfer = (dt: DataTransfer) => Array.from(dt.types || []).includes('Files');

  const handleFileDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasFilesInTransfer(e.dataTransfer)) return;
    fileDragDepthRef.current += 1;
    if (fileDragDepthRef.current === 1) setFileDragOver(true);
  };
  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current -= 1;
    if (fileDragDepthRef.current <= 0) {
      fileDragDepthRef.current = 0;
      setFileDragOver(false);
    }
  };
  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasFilesInTransfer(e.dataTransfer)) e.dataTransfer.dropEffect = 'copy';
  };
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current = 0;
    setFileDragOver(false);
    if (e.dataTransfer.files?.length) addFilesFromList(e.dataTransfer.files);
  };

  const uploadFiles = useCallback(
    async (files: File[]): Promise<CommentAttachment[]> => {
      await auth.authStateReady();
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || firebaseUser.isAnonymous) {
        throw new Error('Firebase sign-in required for file uploads. Use your company Google or email sign-in.');
      }
      const uploaded: CommentAttachment[] = [];
      const n = files.length;
      for (let i = 0; i < n; i++) {
        const file = files[i];
        const fileId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const objectName = buildCommentAttachmentStorageFileName(fileId, file.name);
        const storageRef = ref(storage, `lead-comments/${leadId}/${objectName}`);
        const task = uploadBytesResumable(storageRef, file, { contentType: file.type || 'application/octet-stream' });
        await new Promise<void>((resolve, reject) => {
          task.on(
            'state_changed',
            snap => {
              const fileProgress = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
              setUploadProgress(((i + fileProgress) / n) * 100);
            },
            reject,
            () => resolve(),
          );
        });
        const url = await getDownloadURL(task.snapshot.ref);
        uploaded.push({ id: fileId, name: file.name, url, type: file.type, size: file.size });
        setUploadProgress(((i + 1) / n) * 100);
      }
      return uploaded;
    },
    [leadId],
  );

  /* ── Submit a new comment / reply ─────────────────────────────────────── */
  const submitNewComment = useCallback(
    async (
      payload?: RichCommentEditorPayload,
      options?: { replyToOverride?: LeadFeedComment | null; source?: 'composer' | 'answer' },
    ) => {
      const isAnswer = options?.source === 'answer';
      const editorPayload = payload ?? editorRef.current?.getPayload();
      if (!editorPayload || !user || isSubmitting) return;
      if (editorPayload.isEmpty && (isAnswer || stagedFiles.length === 0)) return;

      setIsSubmitting(true);
      setUploadProgress(0);
      try {
        let uploadedAttachments: CommentAttachment[] = [];
        if (!isAnswer && stagedFiles.length > 0) {
          await auth.currentUser?.getIdToken(true);
          uploadedAttachments = await uploadFiles(stagedFiles);
        }

        const docPayload: Record<string, unknown> = {
          leadId,
          userId: user.id,
          userName: user.name || user.email,
          userInitials: getInitials(user.name || user.email || ''),
          content: editorPayload.bodyPlain,
          bodyHtml: editorPayload.bodyHtml,
          bodyFormat: 'html',
          mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
          createdAt: Timestamp.now(),
        };
        const effectiveReply = options && 'replyToOverride' in options ? options.replyToOverride : replyTo;
        if (effectiveReply?.id) {
          const byId = buildTaskCommentsById(comments as unknown as TaskComment[]);
          docPayload.parentId =
            getTaskCommentThreadRootId(effectiveReply as unknown as TaskComment, byId) || effectiveReply.id;
        }
        if (!isAnswer && composerKind) docPayload.kind = composerKind;

        const created = await addDoc(leadCommentsCollectionRef(leadId), docPayload);

        for (const mention of editorPayload.mentions) {
          if (mention.userId && mention.userId !== user.id) {
            await createMentionNotification({
              leadId,
              leadName: leadName || 'a lead',
              mentionedUserId: mention.userId,
              mentionedUserName: mention.userName,
              fromUserId: user.id,
              fromUserName: user.name || user.email || 'Unknown',
              commentId: created.id,
            });
          }
        }

        if (!isAnswer) {
          editorRef.current?.clear();
          setDraftHtml('');
          setInitialDraftHtml('');
          try {
            window.localStorage.removeItem(draftKey(user?.id, leadId));
          } catch {
            /* ignore */
          }
          setStagedFiles([]);
          setReplyTo(null);
          setComposerKind(null);
          setUploadProgress(0);
        }
        requestAnimationFrame(() => {
          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
      } catch (error: unknown) {
        console.error('[LeadCommentThread] Error adding comment:', error);
        const message = error instanceof Error ? error.message : String(error);
        window.alert(`Could not send comment: ${message}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [comments, composerKind, isSubmitting, leadId, leadName, replyTo, stagedFiles, uploadFiles, user],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = editorRef.current?.getPayload();
    if (!payload || isSubmitting) return;
    if (payload.isEmpty && stagedFiles.length === 0) return;
    if (!untaggedSend.allow(mentionsNotifySomeone(payload.mentions, user?.id), 'click')) return;
    void submitNewComment(payload);
  };
  const handleEditorSubmitShortcut = useCallback(
    (payload: RichCommentEditorPayload) => {
      if (payload.isEmpty && stagedFiles.length === 0) return;
      if (!untaggedSend.allow(mentionsNotifySomeone(payload.mentions, user?.id), 'keyboard')) return;
      void submitNewComment(payload);
    },
    [stagedFiles.length, submitNewComment, untaggedSend, user?.id],
  );

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(leadCommentDocRef(leadId, commentId));
    } catch (error) {
      console.error('[LeadCommentThread] Error deleting comment:', error);
    }
  };

  const startEdit = useCallback((c: LeadFeedComment) => {
    if (!c.id) return;
    setEditingId(c.id);
    const initial =
      c.bodyHtml && c.bodyHtml.length > 0
        ? c.bodyHtml
        : `<p>${(c.body || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br/>')}</p>`;
    setEditingHtml(initial);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingHtml('');
  }, []);

  const saveEdit = useCallback(
    async (commentId: string) => {
      const payload = editEditorRef.current?.getPayload();
      if (!payload || payload.isEmpty || !user) return;
      setEditSaving(true);
      try {
        await updateDoc(leadCommentDocRef(leadId, commentId), {
          content: payload.bodyPlain,
          bodyHtml: payload.bodyHtml,
          bodyFormat: 'html',
          mentions: payload.mentions.length > 0 ? payload.mentions : null,
          editedAt: Timestamp.now(),
          editedById: user.id,
          updatedAt: Timestamp.now(),
        });
        cancelEdit();
      } catch (error) {
        console.error('[LeadCommentThread] Error editing comment:', error);
        window.alert('Could not save edit. Try again.');
      } finally {
        setEditSaving(false);
      }
    },
    [cancelEdit, leadId, user],
  );

  const togglePin = useCallback(
    async (c: LeadFeedComment) => {
      if (!c.id || !user) return;
      try {
        await updateDoc(
          leadCommentDocRef(leadId, c.id),
          c.pinnedAt
            ? { pinnedAt: null, pinnedById: null, updatedAt: Timestamp.now() }
            : { pinnedAt: Timestamp.now(), pinnedById: user.id, updatedAt: Timestamp.now() },
        );
      } catch (error) {
        console.error('[LeadCommentThread] Error toggling pin:', error);
      }
    },
    [leadId, user],
  );

  const toggleLeadReaction = useCallback(
    async (commentId: string, emoji: string) => {
      if (!user?.id) return;
      try {
        await toggleCommentReaction(db, leadCommentDocRef(leadId, commentId), emoji, user.id);
      } catch (error) {
        console.error('[LeadCommentThread] Error toggling reaction:', error);
      }
    },
    [leadId, user?.id],
  );

  const togglePdfFeedReaction = useCallback(
    async (annotationId: string, replyIndex: number | null, emoji: string) => {
      if (!user?.id || !annotationId) return;
      try {
        await togglePdfAnnotationReaction(annotationId, user.id, emoji, replyIndex);
      } catch (error) {
        console.error('[LeadCommentThread] Error toggling PDF reaction:', error);
      }
    },
    [user?.id],
  );

  /* ── PDF plan-pin feed items ──────────────────────────────────────────── */
  const docUrlToName = useMemo(() => {
    const map = new Map<string, string>(leadDocNames);
    comments.forEach(c => {
      c.attachments?.filter(a => a.type === 'application/pdf').forEach(a => map.set(a.url, a.name));
    });
    return map;
  }, [comments, leadDocNames]);

  type PdfFeedItem = {
    type: 'pdf';
    userId: string;
    userName: string;
    content: string;
    createdAt: Date;
    pageNumber?: number;
    docName: string;
    docUrl: string;
    annotationId: string;
    documentId: string;
    isUnread: boolean;
    replyIndex: number | null;
    reactions: CommentReactionsMap;
  };

  const pdfFeedItems = useMemo<PdfFeedItem[]>(() => {
    const items: PdfFeedItem[] = [];
    const currentUid = user?.id;
    const isHttpUrl = (v?: string): boolean => !!v && /^https?:\/\//i.test(v);
    // Agent annotations often store only a Firestore documentId (not a URL).
    // Resolve it to the real download URL so the viewer doesn't fetch the id.
    const resolveDocUrl = (documentUrl?: string, documentId?: string): string => {
      if (isHttpUrl(documentUrl)) return documentUrl!;
      const byId = documentId ? leadDocIdToUrl.get(documentId) : undefined;
      if (byId) return byId;
      if (isHttpUrl(documentId)) return documentId!;
      return documentUrl || documentId || '';
    };
    pdfAnnotations.forEach(ann => {
      const docUrl = resolveDocUrl(ann.documentUrl, ann.documentId);
      const docName =
        docUrlToName.get(docUrl) ||
        docUrlToName.get(ann.documentUrl) ||
        docUrlToName.get(ann.documentId) ||
        'PDF';
      const isUnread = !!currentUid && pdfAnnotationHasUnreadForUser(ann, currentUid);
      items.push({
        type: 'pdf',
        userId: String(ann.userId ?? ''),
        userName: ann.userName,
        content: ann.content,
        createdAt: ann.createdAt,
        pageNumber: ann.pageNumber,
        docName,
        docUrl,
        annotationId: ann.id ?? '',
        documentId: ann.documentId || docUrl,
        isUnread,
        replyIndex: null,
        reactions: normalizeCommentReactions(ann.reactions),
      });
      ann.replies?.forEach((r, replyIdx) => {
        items.push({
          type: 'pdf',
          userId: String(r.userId ?? ''),
          userName: r.userName,
          content: r.content,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt as unknown as string | number),
          pageNumber: ann.pageNumber,
          docName,
          docUrl,
          annotationId: ann.id ?? '',
          documentId: ann.documentId || docUrl,
          isUnread,
          replyIndex: replyIdx,
          reactions: normalizeCommentReactions(r.reactions),
        });
      });
    });
    return items.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, [pdfAnnotations, docUrlToName, leadDocIdToUrl, user?.id]);

  /* ── Filtering + threaded layout ──────────────────────────────────────── */
  const authorFilterOptions = useMemo(() => {
    const m = new Map<string, string>();
    comments.forEach(c => {
      const id = String(c.authorId ?? '').trim();
      if (!id) return;
      if (!m.has(id)) m.set(id, (c.authorName || '').trim() || `${id.slice(0, 8)}…`);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [comments]);

  useEffect(() => {
    if (filter !== 'mine' || !mineAuthorId || mineAuthorId === user?.id) return;
    const ids = new Set(authorFilterOptions.map(o => o.value));
    if (!ids.has(mineAuthorId)) setMineAuthorId('');
  }, [filter, mineAuthorId, authorFilterOptions, user?.id]);

  const commentMatchesFilter = useCallback(
    (c: LeadFeedComment): boolean => {
      if (kindFilter !== 'any' && c.kind !== kindFilter) return false;
      const mineUid = (mineAuthorId || user?.id || '').trim();
      switch (filter) {
        case 'mentions':
          return Array.isArray(c.mentions) && c.mentions.some(m => m.userId === user?.id);
        case 'mine':
          return String(c.authorId ?? '') === String(mineUid);
        case 'attachments':
          return Array.isArray(c.attachments) && c.attachments.length > 0;
        case 'all':
        default:
          return true;
      }
    },
    [filter, kindFilter, mineAuthorId, user?.id],
  );

  const repliesByThreadRoot = useMemo(
    () => groupTaskCommentRepliesByThreadRoot(comments as unknown as TaskComment[]) as unknown as Map<string, LeadFeedComment[]>,
    [comments],
  );

  const visibleTopLevelComments = useMemo(
    () =>
      comments
        .filter(c => !c.parentId)
        .filter(c => {
          if (commentMatchesFilter(c)) return true;
          const threadReplies = c.id ? repliesByThreadRoot.get(c.id) || [] : [];
          return threadReplies.some(reply => commentMatchesFilter(reply));
        }),
    [comments, commentMatchesFilter, repliesByThreadRoot],
  );

  const visiblePinnedComments = useMemo(
    () => comments.filter(c => c.pinnedAt && commentMatchesFilter(c)),
    [comments, commentMatchesFilter],
  );

  type FeedItem = { type: 'comment'; data: LeadFeedComment } | { type: 'pdf'; data: PdfFeedItem };

  const mergedFeed: FeedItem[] = useMemo(() => {
    const regular: FeedItem[] = visibleTopLevelComments.map(c => ({ type: 'comment' as const, data: c }));
    const pdfBaseVisible = (filter === 'all' || filter === 'mine') && kindFilter === 'any';
    const mineUid = (mineAuthorId || user?.id || '').trim();
    const pdfFiltered = pdfBaseVisible
      ? pdfFeedItems.filter(d => (filter === 'all' ? true : String(d.userId ?? '') === String(mineUid)))
      : [];
    const pdf: FeedItem[] = pdfFiltered.map(d => ({ type: 'pdf' as const, data: d }));
    return [...regular, ...pdf].sort((a, b) => a.data.createdAt.getTime() - b.data.createdAt.getTime());
  }, [visibleTopLevelComments, pdfFeedItems, filter, kindFilter, mineAuthorId, user?.id]);

  /* ── Render helpers ───────────────────────────────────────────────────── */
  const renderAttachments = (atts: CommentAttachment[]) => (
    <div className="tc-attachments">
      {atts.map(att => {
        const isPdf = att.type === 'application/pdf';
        const isImg = !!att.type?.startsWith('image/');
        const isCsv = isCsvType(att.type, att.name);
        const chipProps = {
          className: 'tc-attachment-chip',
          title: `${att.name} (${formatFileSize(att.size)})`,
        };
        if (isPdf && onOpenPdf) {
          return (
            <button key={att.id} type="button" {...chipProps} onClick={() => onOpenPdf(att.url, att.name, viewerDocumentIdForAttachment(att))}>
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        if (isImg && onOpenImage) {
          return (
            <button
              key={att.id}
              type="button"
              {...chipProps}
              onClick={() => onOpenImage(att.url, att.name, viewerDocumentIdForAttachment(att), { mimeType: att.type })}
            >
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        if (isCsv && onOpenCsv) {
          return (
            <button
              key={att.id}
              type="button"
              {...chipProps}
              onClick={() => onOpenCsv(att.url, att.name, viewerDocumentIdForAttachment(att), { mimeType: att.type })}
            >
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        return (
          <a key={att.id} {...chipProps} href={att.url} target="_blank" rel="noopener noreferrer">
            <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
            <span className="tc-attachment-name">{att.name}</span>
          </a>
        );
      })}
    </div>
  );

  const kindMeta = (k?: TaskCommentKind) => KIND_OPTIONS.find(o => o.id === k);

  const renderCommentItem = (c: LeadFeedComment, options: { indent?: boolean; isPinnedSection?: boolean; idx: number }) => {
    const { indent, isPinnedSection, idx } = options;
    const isOwn = String(c.authorId ?? '') === String(user?.id ?? '');
    const km = kindMeta(c.kind);
    const isEditing = editingId === c.id;
    const canModerate = !!user && canDeleteTaskComment(user, c as unknown as TaskComment);

    return (
      <div
        key={`${c.id}${isPinnedSection ? '__pinned' : ''}`}
        id={!isPinnedSection && c.id ? `lead-team-comment-${c.id}` : undefined}
        className={`tc-item ${isOwn ? 'tc-item-own' : ''}${indent ? ' tc-item--reply' : ''}${
          c.pinnedAt && !isPinnedSection ? ' tc-item--pinned' : ''
        }`}
        style={{ animationDelay: `${idx * 30}ms` }}
      >
        {indent && <span className="tc-thread-line" aria-hidden />}
        <UserHoverCard
          className="tc-avatar"
          name={c.authorName}
          photoURL={photoForUid(c.authorId)}
          userId={c.authorId}
          crmUser={crmUsers.find(u => u.uid === c.authorId)}
        />
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{c.authorName}</span>
            {km && (
              <span className={`tc-kind-badge tc-kind-badge--${km.id}`} title={`${km.label} comment`}>
                {km.short}
              </span>
            )}
            <span className="tc-item-time">{formatDistanceToNow(c.createdAt, { addSuffix: true })}</span>
            {c.editedAt && (
              <span className="tc-edited-pill" title={`Edited ${format(c.editedAt, 'MMM d, yyyy · h:mm a')}`}>
                edited
              </span>
            )}

            <div className="tc-item-actions">
              {!isPinnedSection && c.id && (
                <button type="button" className="tc-item-iconbtn" onClick={() => setReplyTo(c)} title="Reply" aria-label="Reply">
                  <ReplyIcon size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => togglePin(c)}
                  title={c.pinnedAt ? 'Unpin' : 'Pin to top'}
                  aria-label={c.pinnedAt ? 'Unpin' : 'Pin to top'}
                >
                  {c.pinnedAt ? <PinOff size={11} /> : <Pin size={11} />}
                </button>
              )}
              {isOwn && c.id && !isEditing && (
                <button type="button" className="tc-item-iconbtn" onClick={() => startEdit(c)} title="Edit" aria-label="Edit">
                  <Pencil size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn tc-item-iconbtn--danger"
                  onClick={() => handleDelete(c.id!)}
                  title={isOwn ? 'Delete your comment' : 'Delete comment (moderator)'}
                  aria-label="Delete"
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>

          {isEditing && c.id ? (
            <div className="tc-edit-pane">
              <RichCommentEditor
                ref={editEditorRef}
                initialHtml={editingHtml}
                mentionableUsers={mentionableUsers}
                taskId={editorScopeId}
                onSubmit={() => saveEdit(c.id!)}
                placeholder="Edit your comment…"
                disabled={editSaving}
              />
              <div className="tc-edit-actions">
                <button type="button" className="tc-edit-cancel" onClick={cancelEdit} disabled={editSaving}>
                  Cancel
                </button>
                <button type="button" className="tc-edit-save" onClick={() => saveEdit(c.id!)} disabled={editSaving}>
                  <Check size={12} /> {editSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <RichCommentRenderer className="tc-item-text" bodyHtml={c.bodyHtml} body={c.body} />
          )}

          {user && c.id && !isEditing && (
            <CommentReactions
              classPrefix="tc-"
              reactions={c.reactions || {}}
              currentUserId={user.id}
              onToggle={emoji => toggleLeadReaction(c.id!, emoji)}
              getUserDisplayName={getUserDisplayName}
              revealOnHover
            />
          )}
          {!isEditing && c.attachments && c.attachments.length > 0 && renderAttachments(c.attachments)}
        </div>
      </div>
    );
  };

  const renderPdfItem = (d: PdfFeedItem, idx: number) => {
    const openPdf = () => {
      if (user?.id && d.annotationId) void markAnnotationAsRead(d.annotationId, user.id);
      onOpenPdf?.(d.docUrl, d.docName, d.documentId, d.annotationId ? { annotationId: d.annotationId, pageNumber: d.pageNumber } : undefined);
    };
    return (
      <div
        key={`pdf-${d.annotationId}-${d.replyIndex ?? 'root'}-${idx}`}
        className={`tc-item tc-item-pdf${d.isUnread ? ' tc-item--unread' : ''}`}
        style={{ animationDelay: `${idx * 30}ms` }}
      >
        <div className="tc-avatar tc-avatar-pdf">
          <FileText size={12} />
        </div>
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{d.userName}</span>
            <span className="tc-item-badge-pdf">PDF{d.pageNumber != null ? ` p.${d.pageNumber}` : ''}</span>
            <span className="tc-item-time">{formatDistanceToNow(d.createdAt, { addSuffix: true })}</span>
          </div>
          <div className="tc-item-text">{d.content}</div>
          {user && d.annotationId && (
            <div className="tc-pdf-reactions" onClick={e => e.stopPropagation()} role="presentation">
              <CommentReactions
                classPrefix="tc-"
                reactions={d.reactions || {}}
                currentUserId={user.id}
                onToggle={emoji => togglePdfFeedReaction(d.annotationId, d.replyIndex, emoji)}
                getUserDisplayName={getUserDisplayName}
                revealOnHover
              />
            </div>
          )}
          {onOpenPdf && (
            <button type="button" className="tc-pdf-link" onClick={openPdf}>
              Open {d.docName}
            </button>
          )}
        </div>
      </div>
    );
  };

  const sectionClass = [
    'tc-section',
    'lct-section',
    compact ? 'tc-compact' : '',
    compact && isExpanded ? 'tc-expanded' : '',
    isPopout ? 'tc-section--popout' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const showBody = !compact || isExpanded || isPopout;

  return (
    <>
      {isPopout && <div className="tc-popout-backdrop" onClick={() => setIsPopout(false)} aria-hidden />}
      <div className={sectionClass}>
        {compact && !isPopout && (
          <button className="tc-collapse-header" onClick={() => setIsExpanded(v => !v)}>
            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span className="tc-collapse-label">Team Notes</span>
            <span className="tc-collapse-count">{comments.length}</span>
          </button>
        )}

        {isPopout && (
          <div className="tc-popout-header">
            <span className="tc-popout-title">{leadName ? `Team Notes · ${leadName}` : 'Team Notes'}</span>
            <span className="tc-popout-count">{comments.length}</span>
            <button type="button" className="tc-popout-close" onClick={() => setIsPopout(false)} title="Close (Esc)" aria-label="Close popup">
              <X size={14} />
            </button>
          </div>
        )}

        {showBody && renderFullUI()}
      </div>
    </>
  );

  function renderFullUI() {
    const sendDisabled = isSubmitting || ((!draftHtml || draftHtml === '<p></p>') && stagedFiles.length === 0);

    return (
      <div
        className={`tc-drop-zone ${fileDragOver ? 'tc-drop-zone--active' : ''}`}
        onDragEnter={handleFileDragEnter}
        onDragLeave={handleFileDragLeave}
        onDragOver={handleFileDragOver}
        onDrop={handleFileDrop}
      >
        {fileDragOver && (
          <div className="tc-drop-overlay" aria-hidden>
            <Paperclip size={28} strokeWidth={1.75} />
            <span>Drop files to attach</span>
          </div>
        )}

        {comments.length > 0 && (
          <div className="tc-filter-chips" aria-label="Filter notes">
            <div className="tc-filter-chip-buttons" role="tablist">
              {SCOPE_FILTER_CHIPS.map(f => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`tc-filter-chip${filter === f.id ? ' tc-filter-chip--active' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              className={`tc-filter-chip tc-filter-mine-select${filter === 'mine' ? ' tc-filter-chip--active' : ''}`}
              aria-label="Filter by author"
              value={filter === 'mine' ? mineAuthorId || user?.id || '' : ''}
              onChange={e => {
                const v = e.target.value;
                if (!v) {
                  setFilter('all');
                  return;
                }
                setMineAuthorId(v);
                setFilter('mine');
              }}
            >
              <option value="">Author</option>
              {user?.id ? <option value={user.id}>Mine</option> : null}
              {authorFilterOptions
                .filter(o => o.value !== user?.id)
                .map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
            <div className="tc-filter-trailing">
              <select
                className="tc-filter-kind"
                value={kindFilter}
                onChange={e => setKindFilter(e.target.value as TaskCommentKind | 'any')}
                aria-label="Filter by kind"
              >
                <option value="any">All kinds</option>
                {KIND_OPTIONS.map(k => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="tc-feed" ref={scrollRef}>
          {visiblePinnedComments.length > 0 && (
            <div className="tc-pinned-section" aria-label="Pinned notes">
              <div className="tc-pinned-header">
                <Pin size={11} aria-hidden />
                <span>Pinned</span>
              </div>
              {visiblePinnedComments.map((c, i) => renderCommentItem(c, { isPinnedSection: true, idx: i }))}
            </div>
          )}

          {mergedFeed.length === 0 && visiblePinnedComments.length === 0 && (
            <div className="tc-empty">
              {comments.length === 0 ? 'No team notes yet. Start the conversation.' : 'No notes match the current filter.'}
            </div>
          )}

          {mergedFeed.map((item, idx) => {
            if (item.type === 'pdf') return renderPdfItem(item.data, idx);
            const c = item.data;
            const replies = c.id ? repliesByThreadRoot.get(c.id) || [] : [];
            const visibleReplies = replies.filter(r => commentMatchesFilter(r));
            return (
              <React.Fragment key={c.id}>
                {renderCommentItem(c, { idx })}
                {visibleReplies.map((r, ri) => renderCommentItem(r, { indent: true, idx: idx + ri + 1 }))}
                {c.kind === 'question' && c.id ? (
                  <QuestionAnswerSlot
                    questionId={c.id}
                    hasReplies={visibleReplies.length > 0}
                    mentionableUsers={mentionableUsers}
                    taskId={editorScopeId}
                    disabled={isSubmitting}
                    onSubmit={async payload => {
                      await submitNewComment(payload, { replyToOverride: c, source: 'answer' });
                    }}
                    confirmWhenUntagged
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>

        <form className="tc-composer" onSubmit={handleSubmit}>
          <CommentTypingIndicator label={typingLabel} />
          {replyTo && (
            <div className="tc-reply-chip">
              <CornerDownRight size={12} />
              <span className="tc-reply-chip-label">
                Replying to <strong>{replyTo.authorName}</strong>:
              </span>
              <span className="tc-reply-chip-excerpt">{excerptOf(replyTo)}</span>
              <button type="button" className="tc-reply-chip-clear" onClick={() => setReplyTo(null)} title="Cancel reply" aria-label="Cancel reply">
                <X size={12} />
              </button>
            </div>
          )}

          {stagedFiles.length > 0 && (
            <div className="tc-composer-attachments">
              {stagedFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} className="tc-staged-file">
                  <span className="tc-staged-icon">{getFileIcon(f.type)}</span>
                  <span className="tc-staged-name">{f.name}</span>
                  <button type="button" className="tc-staged-remove" onClick={() => removeStagedFile(i)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="tc-upload-bar">
              <div className="tc-upload-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}

          <RichCommentEditor
            ref={editorRef}
            initialHtml={initialDraftHtml}
            mentionableUsers={mentionableUsers}
            taskId={editorScopeId}
            onChange={setDraftHtml}
            onSubmit={handleEditorSubmitShortcut}
            placeholder={leadName ? `Write a team note on ${leadName}…` : 'Write a team note… (@ to mention, ⌘/Ctrl-Enter to send)'}
            disabled={isSubmitting}
          />

          <div className="tc-composer-row">
            <button
              type="button"
              className={`tc-question-toggle${composerKind === 'question' ? ' tc-question-toggle--on' : ''}`}
              onClick={() => setComposerKind(k => (k === 'question' ? null : 'question'))}
              aria-pressed={composerKind === 'question'}
              title="Ask a question — anyone in this thread can answer underneath"
            >
              Question
            </button>
            <select
              className="tc-kind-select"
              value={composerKind ?? ''}
              onChange={e => setComposerKind((e.target.value || null) as TaskCommentKind | null)}
              aria-label="Note kind"
              title="Tag this note"
            >
              <option value="">No tag</option>
              {KIND_OPTIONS.map(k => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>

            <div className="tc-composer-spacer" />

            <input type="file" ref={fileInputRef} className="tc-file-input" multiple onChange={handleFileSelect} />
            <button
              type="button"
              className="tc-btn-attach"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
              disabled={isSubmitting}
            >
              <Plus size={14} />
            </button>
            <UntaggedSendHint open={untaggedSend.open} onDismiss={untaggedSend.dismiss}>
            <button type="submit" className="tc-btn-send" title={isSubmitting ? 'Sending…' : 'Send (⌘/Ctrl-Enter)'} disabled={sendDisabled}>
              <Send size={14} />
            </button>
            </UntaggedSendHint>
          </div>
        </form>
      </div>
    );
  }
};

export default LeadCommentThread;

